import React, { useCallback, useEffect, useState } from 'react';
import { FormInstance } from 'antd';
import { Colors, FontWeight } from '@common/tokens';
import {
  getApprovalChainTitle,
  getLetterSignByNumber,
  getNumberPositionSufix,
} from '@approvals/services/approvals-service';
import {
  Banner,
  CommentInput,
  InfoTooltip,
  LoadingState,
  Radio,
  UploadDragger,
  Form,
} from '@common/components';
import {
  ApprovalChainTemplateSelectStepTemplateType,
  ApprovalsConfigQuery,
  ApprovalsConfigQueryResponse,
  AprovalChainTemplateSelectQuery,
  AprovalChainTemplateSelectTemplateType,
} from '@approvals/graphql';
import { useCurrentMembership } from '@identity/hooks';
import { useQuery } from '@common/hooks';

const ByPassApprovalOption = () => (
  <>
    <Radio key="bypass" value="bypass" className="radio-option">
      Bypass Approval
    </Radio>
    <p className="radio-option-description">
      This request does not require approval
    </p>
  </>
);

const PreapprovalOption = () => (
  <>
    <Radio key="preapproved" value="preapproved" className="radio-option">
      Preapproved
    </Radio>
    <p className="radio-option-description">
      This request does not require approval
    </p>
  </>
);

const RadioLevelDescription = ({
  stepTemplates,
}: {
  stepTemplates: ApprovalChainTemplateSelectStepTemplateType[];
}) => {
  const levels = stepTemplates.map((step, index) => {
    const isLastLevel = index + 1 === stepTemplates.length;

    return (
      <div className="radio-option-description d-flex" key={step.id}>
        <span className="radio-option-level">
          {`${getNumberPositionSufix(index + 1)} Level`}
        </span>
        <div>
          {step.approvalChainApproverTemplates.map((approver) => {
            return (
              <span key={approver.id} className="option-label option-name">
                {approver.name}
              </span>
            );
          })}
          {!isLastLevel && <hr className="divider-between-level" />}
        </div>
      </div>
    );
  });

  return levels;
};

const RadioOptionForTemplate = ({
  template,
  index,
}: {
  template: AprovalChainTemplateSelectTemplateType;
  index: number;
}) => (
  <div className="approval-template-select-box" key={template.id}>
    <Radio key={template.id} value={template.id} className="radio-option">
      {getApprovalChainTitle(getLetterSignByNumber(index))}
    </Radio>
    <RadioLevelDescription
      stepTemplates={template.approvalChainStepTemplates}
    />
    <hr className="divider-between-chain" />
  </div>
);

export const ApprovalChainConfig = ({
  isApprovalNotesRequired = true,
  organizationUnitId,
  actionType,
  form,
  disableApprovalChainSelection,
}: {
  isApprovalNotesRequired?: boolean;
  organizationUnitId: string;
  actionType: string;
  form: FormInstance;
  disableApprovalChainSelection?: boolean;
}) => {
  const setApprovalChainTemplateSource = useCallback(
    (value: string) => {
      form.setFieldsValue({ approvalChainTemplateSource: value });
    },
    [form]
  );

  const currentMembership = useCurrentMembership();
  const extensionOfferAction = actionType === 'extension_offer_active';

  const { approvalChainTemplateSource } = form.getFieldsValue();
  const [approvalChainTemplateIdState, setApprovalChainTemplateIdState] =
    useState<string | null>(null);

  useEffect(() => {
    if (!approvalChainTemplateSource && approvalChainTemplateIdState) {
      setApprovalChainTemplateSource(approvalChainTemplateIdState);
    }
  }, [
    approvalChainTemplateIdState,
    setApprovalChainTemplateSource,
    approvalChainTemplateSource,
  ]);

  const draggerProps = {
    name: 'approvalDocument',
    action: '',
    beforeUpload() {
      return false;
    },
  };

  const { loading, data } = useQuery(AprovalChainTemplateSelectQuery, {
    variables: {
      organizationUnitUuid: organizationUnitId,
      actionType: actionType,
    },
  });

  const { loading: approvalsConfigLoading, data: approvalsConfigData } =
    useQuery<ApprovalsConfigQueryResponse>(ApprovalsConfigQuery);

  if (loading || approvalsConfigLoading || !data || !approvalsConfigData)
    return <LoadingState />;

  if (data.approvalChainTemplates.length === 0) {
    return null;
  }

  const isPreapproved =
    data?.organizationUnit.preapprovalActions?.includes(actionType);
  const requireStaffingRequestDocument =
    approvalsConfigData?.approvalConfigs?.requireStaffingRequestDocument &&
    actionType === 'staffing_request_open';
  const requireExtensionOfferDocument =
    approvalsConfigData?.approvalConfigs?.requireExtensionOfferDocument &&
    actionType === 'extension_offer_active';
  const documentNote = extensionOfferAction
    ? approvalsConfigData?.approvalConfigs?.extensionOfferDocumentNote
    : approvalsConfigData?.approvalConfigs?.staffingRequestDocumentNote;

  const canApprovalsBypass = currentMembership.canApprovalsBypass;

  const ExtraRadioOption = () => {
    if (isPreapproved) {
      setApprovalChainTemplateIdState('preapproved');
      return <PreapprovalOption />;
    }
    if (canApprovalsBypass) {
      setApprovalChainTemplateIdState('bypass');
      return <ByPassApprovalOption />;
    }

    return null;
  };

  if (
    !isPreapproved &&
    !approvalChainTemplateSource &&
    !approvalChainTemplateIdState &&
    data.approvalChainTemplates?.length > 0
  ) {
    const firstApprovalChainTemplateId = data.approvalChainTemplates[0].id;

    setApprovalChainTemplateIdState(firstApprovalChainTemplateId || null);
  }

  return (
    <>
      {!isPreapproved && (
        <Form.Item
          label="Approvers"
          name="approvalChainTemplateSource"
          rules={[
            { required: true, message: 'Please choose an approval option' },
          ]}
        >
          <Radio.Group
            className="ApprovalChainTemplateSelect"
            disabled={disableApprovalChainSelection}
          >
            {data.approvalChainTemplates.map(
              (
                approvalTemplate: AprovalChainTemplateSelectTemplateType,
                index: number
              ) => (
                <RadioOptionForTemplate
                  key={approvalTemplate.id}
                  template={approvalTemplate}
                  index={index}
                />
              )
            )}
            <ExtraRadioOption />
          </Radio.Group>
        </Form.Item>
      )}
      {!isPreapproved && (
        <CommentInput
          label="Approval Notes"
          required={isApprovalNotesRequired}
          placeholder="Add any additional info for the approvers"
        />
      )}
      {data.approvalChainTemplates &&
        (requireStaffingRequestDocument || requireExtensionOfferDocument) && (
          <>
            <>
              <label style={{ color: 'red' }}>* </label>
              Approval Document
              <InfoTooltip
                size="md"
                leftOffset="sm"
                title="Your organization requires an attachment to submit staffing requests."
              />
            </>
            {documentNote && (
              <Banner
                showIcon={false}
                type="success"
                style={{ marginTop: 5, marginBottom: 8 }}
              >
                {documentNote}
              </Banner>
            )}
            <Form.Item
              name="approvalDocument"
              valuePropName="approvalDocument"
              key="approvalDocument"
              rules={[
                {
                  required: true,
                  message: 'Please upload a document',
                },
              ]}
            >
              <UploadDragger multiple={false} {...draggerProps} />
            </Form.Item>
          </>
        )}
      <style jsx>
        {`
          :global(.ApprovalChainTemplateSelect) {
            display: block;
          }
          :global(.ApprovalChainTemplateSelect .radio-option) {
            padding-bottom: 8px;
          }
          :global(.ApprovalChainTemplateSelect .radio-option-description) {
            padding-left: 24px;
          }
          :global(.ApprovalChainTemplateSelect .ant-radio-wrapper) {
            font-weight: ${FontWeight.bold};
          }
          :global(.ApprovalChainTemplateSelect .option-label) {
            display: block;
            margin-left: 16px;
          }
          :global(.ApprovalChainTemplateSelect .option-name):not(
              :last-of-type
            ):after {
            content: 'OR';
            color: ${Colors.midnightLighter};
            padding-left: 8px;
          }
          :global(.divider-between-chain) {
            border-top: 1px solid ${Colors.steel};
            border-bottom: none;
            margin: 16px 0;
          }
          :global(.divider-between-level) {
            margin: 8px 0px 8px 16px;
            border-top: 1px dashed ${Colors.steel};
            border-bottom: none;
          }
          :global(.radio-option-level) {
            color: ${Colors.midnightLighter};
          }
        `}
      </style>
    </>
  );
};
